// @vendors
import React, { useEffect, useMemo, useState } from "react";
import sx from "mui-sx";
import { Checkbox, FormControlLabel } from "@mui/material";
import { isNull as _isNull } from "lodash";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

// @assets
import { EmployeeTableStyles, UserManagementStyle } from "../../../../../assets/css/employeeTable-style.js";
import { theme } from "../../../../../assets/theme";

// @material
import {
  Box,
  Collapse,
  TableCell,
  TableRow,
  ExpandMoreIcon,
  IconButton,
  Switch,
} from "../../../../../components/shared/MaterialUI.js";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

// @components
import EmployeeDetail from "./EmployeeDetail.js";
import WotcBadge from "../../../../../components/atoms/WotcBadge.jsx";
import ConfirmationDialog from "../../../../../components/dialog/DialogConfirm.js";

// @helpers
import AppHelper from "../../../../../helpers/AppHelper.js";
import {
  convertDateToUTCTexas,
  getTargetGroups,
  HtmlTooltip,
  isRyzeOrganization,
  permissionsForAddOrEditEmployees,
} from "../../../../../helpers/FunctionsUtils.js";
import { LISTROLES } from "../../../../../helpers/ListRoles.js";

// @state
import { useUI } from "../../../../../app/context/ui.js";

// @service
import EmployeeCompanyServiceNewApi from "../../../../../services/newApi/EmployeeCompanyServiceNewApi.js";
import { ROUTENAME } from "../../../../../navigation/RouteName.js";

// @hooks
import UseRowDetail from "../../../hooks/UseRowDetail.js";

const EmployeeRow = (props) => {
  const { row } = props;
  const { blockUI, snackbarUI } = useUI();
  const styles = useMemo(() => EmployeeTableStyles(), []);
  const style = useMemo(() => UserManagementStyle(), []);
  const employeeServiceNewApi = useMemo(() => new EmployeeCompanyServiceNewApi(), []);
  const globalState = useSelector((state) => state);
  const stateDetail = globalState?.employeeSelected;
  const user = globalState?.user;
  const organization = globalState?.organizationReducer?.organizationSelected;
  const [certification, setCertification] = useState('');
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [checkSelected, setCheckSelected] = useState(null);
  const history = useHistory();

  const {
    employeeInfo,
    open,
    handleOpen,
  } = UseRowDetail(row);

  //add color if amount has value
  const bgColor = (wotc = true) => {
    if (wotc) return _isNull(row?.amount) ? '#9ea19a' : row?.amount === 0 ? 'red' : 'green';
    else return _isNull(row?.submitterPreprocessStatus) ? '#9ea19a' : row?.submitterPreprocessStatus ? 'green' : 'red';
  }

  const titleTooltip = () => {
    const groups = getTargetGroups(row);
    if (groups) {
      return groups?.split(', ').map((tile, index) => (
        <p key={index} style={{ color: theme.palette.secondary.black }}>{tile}</p>
      ))
    }
    return '';
  }

  const wotcBadge = () => {
    const bgColor = _isNull(row?.isElegible) ? '#9ea19a' : row?.isElegible ? 'green' : 'red';
    return (
      <WotcBadge bgColor={bgColor} />
    )
  }

  useEffect(() => {
    setCertification(row?.stateCertification)
    setCheckSelected(row?.stateCertification)
  }, [row])

  const handleChangeCheck = (e) => {
    if(isRyzeOrganization(organization))return;
    setCheckSelected('')
    const check = e?.target
    if (check.checked) {
      setCheckSelected(check.value)
      if (!certification) return stateCertificationEmpty(check.value)
      setOpenModalConfirm(true)
    } else {
      setCheckSelected(null)
      setOpenModalConfirm(true)
    }
  }

  const stateCertificationEmpty = (value) => {
    const objectToUpdate = {
      stateCertification: value,
      liveEz: row?.liveEz,
      summerYouthEmpZone: row?.summerYouthEmpZone,
      liveRrc: row?.liveRrc
    }
    submitEmployeeByProp(objectToUpdate)
    setCertification(value)
  }

  // call after close confirmation modal on state cerfication
  const handleClose = (newValue) => {
    setOpenModalConfirm(false);
    if (newValue === 'yes') {
      const objectToUpdate = {
        stateCertification: checkSelected,
        liveEz: row?.liveEz,
        summerYouthEmpZone: row?.summerYouthEmpZone,
        liveRrc: row?.liveRrc
      }
      submitEmployeeByProp(objectToUpdate)
      setCertification(checkSelected)
    }
    if (newValue === 'no') return
  };

  const handleChangeCheckEz = (e, type) => {
    if(isRyzeOrganization(organization))return;
    const objectToUpdate = {
      stateCertification: row?.stateCertification,
      liveEz: row?.liveEz,
      summerYouthEmpZone: row?.summerYouthEmpZone,
      liveRrc: row?.liveRrc
    }
    if (type === 'ez') return submitEmployeeByProp({ ...objectToUpdate, liveEz: e.target.checked })
    if (type === 'sy') return submitEmployeeByProp({ ...objectToUpdate, summerYouthEmpZone: e.target.checked })
    return submitEmployeeByProp({ ...objectToUpdate, liveRrc: e.target.checked })
  }

  const submitEmployeeByProp = async (prop) => {
    try {
      blockUI.current.open(true);
      await employeeServiceNewApi.editEmployeeByProp(row?.companyId, row?.id, prop);
      const params = {
        page: props.pages?.currentPage,
        size: props.pages?.pageSize,
        filters: props.search
      }
      props.getAllEmployesByCompany(params);
    } catch (e) {
      blockUI.current.open(false);
      AppHelper.checkError(e, snackbarUI);
    }
  }

  const convertModifiedDate = () => {
    return convertDateToUTCTexas(row?.modifiedLast)
  }

  const showDashboardStatus = () => {
    if (!row?.dashboardStatus) return (<Box sx={styles.qStatusBox}>Sent</Box>)
    if (row?.status === 'filled_out') return (
      <Box sx={styles.qStatusBox}>
        {row?.dashboardStatus} <span style={{ color: 'red' }}>*</span>
      </Box>
    )
    return (<Box sx={styles.qStatusBox}>{row?.dashboardStatus}</Box>)
  }

  const getRamainingDay = () => {
    if (row?.remainDay < 0) return 0;
    if (row?.remainDay === '0') return 'Today';
    return row?.remainDay || "-";
  }

  const handleDelete = async () => {
    if(isRyzeOrganization(organization))return;
    try {
      blockUI.current.open(true);
      row?.isDeleted ? await employeeServiceNewApi.activeEmployee(row?.companyId, row?.id) : await employeeServiceNewApi.deleteEmployee(row?.companyId, row?.id);
      successDelete();
    } catch (e) {
      blockUI.current.open(false);
      AppHelper.checkError(e, snackbarUI);
    }
  }

  const successDelete = async () => {
    const params = {
      page: props.pages?.currentPage,
      size: props.pages?.pageSize,
      filters: { ...props.search, IsDeleted: row?.isDeleted },
      stats: row?.isDeleted ? false : true,
    }
    props.getAllEmployesByCompany(params);
  }

  const permissionsInactiveEmployee = (role) => {
    if (role === LISTROLES.superAdmin || role === LISTROLES.orgManager) return true;
    return false;
  }

  const tooltipIconDelete = () => {
    if (row?.isDeleted) return 'Active employee';
    return 'You are about to delete this employee from the board, but you can reactivate it in the deleted employees section.'
  }

  const allowToEditStateCertification = () => {
    const editableRoles = new Set([
      LISTROLES.superAdmin,
      LISTROLES.orgManager,
      LISTROLES.orgReporter,
      LISTROLES.orgProcessor,
    ]);

    return editableRoles.has(user?.role);
  }

  const tooltipPreprocessLogs = () => {
    return (
      <>
        {row?.preprocessLogs?.map((rw, i) => {
          return ( <p key={i}>{rw.message}</p> );
        })}
      </>
    )
  }

  return (
    <>
      <TableRow>
        <TableCell
          scope="row"
          sx={sx(
            styles.tableCell,
            { condition: open, sx: styles.borderNone },
            { condition: !open, sx: styles.bottomBorder }
          )}
        >
          <IconButton onClick={handleOpen}>
            <ExpandMoreIcon sx={{ color: theme.palette.primary.black }} />
          </IconButton>
        </TableCell>
        {
          props.isSearch && <TableCell
            scope="row"
            sx={sx( styles.tableCellCompany)}
            onClick={() => history.push(`${ROUTENAME.employer}${row?.companyId}`)}
          >
            {row?.company}
          </TableCell>
        }

        <TableCell
          scope="row"
          sx={sx( styles.tableCell )}
        >
          {row.fullName}
        </TableCell>

        <TableCell
          scope="row"
          sx={sx(styles.tableCellLastModified)}
        >
          {convertModifiedDate()}
        </TableCell>

        <TableCell
          sx={sx(
            styles.tableCell,
            styles.tableColumnHide
          )}
        >
          {showDashboardStatus()}
        </TableCell>

        <TableCell
          sx={
            (sx(
              styles.tableCell,
              styles.tableColumnHide
            ),
              sx({
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.2em",
                color: row?.remainDay <= 5 ? "red" : theme.palette.secondary.black,
              }, styles.tableColumnHide))
          }
        >
          {getRamainingDay()}
        </TableCell>

        <TableCell sx={(sx(styles.tableCell, styles.tableColumnHide ))} >
          <Box sx={{display: 'flex', cursor: 'pointer'}}>
            <HtmlTooltip title={titleTooltip() ?? ''}>
              { getTargetGroups(row).length > 30 ? getTargetGroups(row).substring(0, 30) + '...' : getTargetGroups(row)}
            </HtmlTooltip>
          </Box>
        </TableCell>

        <TableCell sx={sx({ textAlign: 'center' }, styles.tableColumnHide)}>
          <div style={styles.containerCheck} >
            <FormControlLabel
              checked={row?.isNsi}
              sx={{ margin: 0, pointerEvents: 'none' }}
              control={
                <Checkbox
                  sx={styles.checkboxDefault}
                  color="success"
                />
              }
            />
          </div>
        </TableCell>

        <TableCell sx={sx({ textAlign: 'center' }, styles.tableColumnHide)}>
          {
            (row?.qualifie || row?.subQualifie)
              ? (<HtmlTooltip title={titleTooltip() ?? ''}>
                  <Box component="span" sx={{ display: 'inline-block', cursor: 'pointer'}}>
                    {wotcBadge()}
                  </Box>
                </HtmlTooltip>)
              : (wotcBadge())
          }
        </TableCell>

        <TableCell sx={sx({ textAlign: 'center' }, styles.tableColumnHide)}>
          {row?.submitterPreprocessStatus || _isNull(row?.submitterPreprocessStatus)
            ? (
                <Box component="span" sx={{ display: 'inline-block'}}>
                  {<WotcBadge bgColor={bgColor(false)} />}
                </Box>
              )
            : (
                <HtmlTooltip title={ tooltipPreprocessLogs() ?? '' }>
                  <Box component="span" sx={{ display: 'inline-block'}}>
                    {<WotcBadge bgColor={bgColor(false)} />}
                  </Box>
                </HtmlTooltip>
              )
          }
        </TableCell>

        <TableCell sx={sx({ textAlign: 'center' }, styles.tableColumnHide)}>
          <div style={{ display: 'flex', gap: 0 }}>
            <FormControlLabel
              checked={row?.stateCertification === 'Yes'}
              sx={{
                margin: 0,
                pointerEvents: row?.isDeleted ? 'none' : allowToEditStateCertification() ? 'auto' : 'none',
              }}
              value='Yes'
              control={
                <Checkbox
                  sx={styles.checkboxDefault}
                  color="success"
                  onChange={(e) => handleChangeCheck(e)}
                />
              }
            />
            <FormControlLabel
              checked={row?.stateCertification === 'No'}
              sx={{
                margin: 0,
                pointerEvents: row?.isDeleted ? 'none' : allowToEditStateCertification() ? 'auto' : 'none',
              }}
              value='No'
              control={
                <Checkbox
                  sx={styles.checkboxDefault}
                  color="success"
                  onChange={(e) => handleChangeCheck(e)}
                />
              }
            />
            <FormControlLabel
              checked={row?.stateCertification === 'Pending'}
              sx={{
                margin: 0,
                pointerEvents: row?.isDeleted ? 'none' : allowToEditStateCertification() ? 'auto' : 'none',
              }}
              value='Pending'
              control={
                <Checkbox
                  sx={styles.checkboxDefault}
                  color="success"
                  onChange={(e) => handleChangeCheck(e)}
                />
              }
            />
            <ConfirmationDialog
              id="confirm-dialog"
              keepMounted
              open={openModalConfirm}
              onClose={handleClose}
              view="default"
            />
          </div>
        </TableCell>

        <TableCell sx={sx({ textAlign: 'center' }, styles.tableColumnHide)}>
          <div style={{ display: 'flex', gap: 0, alignItems: 'center', justifyContent: 'center' }}>
            <FormControlLabel
              checked={row?.liveEz}
              sx={{
                margin: 0,
                pointerEvents: row?.isDeleted ? 'none' : permissionsForAddOrEditEmployees(user?.role) ? 'auto' : 'none',
              }}
              control={
                <Checkbox
                  sx={styles.checkboxDefault}
                  color="success"
                  onChange={(e) => handleChangeCheckEz(e, 'ez')}
                />
              }
            />
          </div>
        </TableCell>

        <TableCell sx={sx({ textAlign: 'center' }, styles.tableColumnHide)}>
          <div
            style={{
              display: 'flex',
              gap: 0,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FormControlLabel
              checked={row?.liveRrc}
              sx={{
                margin: 0,
                pointerEvents: row?.isDeleted ? 'none' : permissionsForAddOrEditEmployees(user?.role) ? 'auto' : 'none',
              }}
              control={
                <Checkbox
                  sx={styles.checkboxDefault}
                  color="success"
                  onChange={(e) => handleChangeCheckEz(e, 'rrc')}
                />
              }
            />
          </div>
        </TableCell>

        <TableCell sx={sx({ textAlign: 'center' }, styles.tableColumnHide)}>
          <div style={{ display: 'flex', gap: 0, alignItems: 'center', justifyContent: 'center' }}>
            <FormControlLabel
              checked={row?.summerYouthEmpZone}
              sx={{
                margin: 0,
                pointerEvents: row?.isDeleted ? 'none' : permissionsForAddOrEditEmployees(user?.role) ? 'auto' : 'none',
              }}
              control={
                <Checkbox
                  sx={styles.checkboxDefault}
                  color="success"
                  onChange={(e) => handleChangeCheckEz(e, 'sy')}
                />
              }
            />
          </div>
        </TableCell>

        {permissionsInactiveEmployee(user?.role)
          ? <TableCell
              sx={sx(
                styles.tableCell,
                styles.tableColumnHide
              )}
            >
              <HtmlTooltip title={tooltipIconDelete()}>
                {
                  row?.isDeleted ? (
                    <Switch
                      checked={!row?.isDeleted}
                      onChange={() => handleDelete()}
                      slotProps={{ input: { 'aria-label': 'controlled' } }}
                      sx={style.switch}
                    />
                  ) : (
                    <IconButton
                      onClick={handleDelete}
                      sx={{ color: theme.palette.secondary.black, '&:hover': { backgroundColor: theme.cards.iconBackground } }}
                    >
                      <DeleteOutlineOutlinedIcon />
                    </IconButton>
                  )
                }
              </HtmlTooltip>
            </TableCell>
          : null
        }
      </TableRow>

      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0, width: '100%'}}
          sx={sx(
            styles.tableCell,
            { padding: "0" },
            { condition: !open, sx: styles.borderNone },
            { condition: open, sx: styles.bottomBorder }
          )}
          colSpan={14}
        >
          <Collapse
            in={stateDetail?.detail === row?.id ? true : false}
            //in={open}
            timeout="auto"
            unmountOnExit
            sx={{ padding: "0 100px", [theme.breakpoints.down('md')]: { padding: '0 10px' } }}
          >
            <EmployeeDetail
              employeeInfo={employeeInfo}
              pages={props.pages}
              getAllEmployesByCompany={props.getAllEmployesByCompany}
              search={props.search}
              companyId={row?.companyId}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default EmployeeRow;
